import React, { useState, useEffect } from 'react';
import { Card as CardType, Comment, CardActivity, Attachment, commentApi, activityApi, cardApi, attachmentApi, getCardApiErrorMessage, wasBoardAccessDenied } from '../services/api';
import { AttachmentList } from './AttachmentList';
import { LabelManager } from './LabelManager';
import { CardAssignments } from './CardAssignments';
import { ConfirmModal } from './ConfirmModal';
import { subscribeToCommentEvents } from '../services/commentEventBus';
import { applyCommentCreated, applyCommentUpdated, applyCommentDeleted } from '../services/commentEvents';
import { subscribeToCardModalEvents } from '../services/cardModalEventBus';
import { applyCardActivityCreated } from '../services/cardActivityEvents';
import { Xmark, EditPencil, Trash, Plus, Page, Check, BoxIso, Link, Download } from 'iconoir-react';
import ReactMarkdown from 'react-markdown';
import jsPDF from 'jspdf';
import {useAuth} from '../contexts/AuthContext';

const LABEL_COLORS = [
  { value: '#75D494', name: 'Vert' },
  { value: '#EDD87A', name: 'Jaune' },
  { value: '#EDB85C', name: 'Orange' },
  { value: '#EDA8A1', name: 'Rouge/Rose' },
  { value: '#C7A8ED', name: 'Violet' },
  { value: '#85BFE0', name: 'Bleu' },
  { value: '#A3DBE8', name: 'Cyan' },
  { value: '#BED6A3', name: 'Vert clair' },
  { value: '#EDAABF', name: 'Rose' },
  { value: '#A8B8BF', name: 'Gris' },
];

interface CardModalProps {
  card: CardType;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (card: CardType) => void;
  onDelete: (cardId: number) => void;
  onArchive: (cardId: number, archived: boolean) => void;
  userRole?: string;
  boardId?: number;
  onRefresh?: () => void;
}

export const CardModal: React.FC<CardModalProps> = ({ card, isOpen, onClose, onUpdate, onDelete, onArchive, userRole = 'visitor', boardId, onRefresh }) => {
  const { user } = useAuth();
  const canEdit = (userRole === 'admin' || userRole === 'editor') && !card.archived;
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description || '');
  const [labelColor, setLabelColor] = useState(card.label_color || '');
  const [startDate, setStartDate] = useState(card.start_date || '');
  const [dueDate, setDueDate] = useState(card.due_date || '');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [activities, setActivities] = useState<CardActivity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<number | null>(null);
  const [showDeleteCardConfirm, setShowDeleteCardConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadComments();
      loadActivities();
      loadAttachments();
    }
  }, [isOpen, card.id]);

  // Abonné uniquement pendant l'ouverture : évite des listeners inactifs sur
  // chaque carte du board ; loadComments() ci-dessus rattrape le reste à la réouverture.
  useEffect(() => {
    if (!isOpen) return;

    const unsubscribe = subscribeToCommentEvents((busEvent) => {
      if (boardId !== undefined && busEvent.event.board_id !== boardId) return;

      switch (busEvent.type) {
        case 'created':
          setComments((prev) => applyCommentCreated(prev, card.id, busEvent.event));
          break;
        case 'updated':
          setComments((prev) => applyCommentUpdated(prev, card.id, busEvent.event));
          break;
        case 'deleted':
          setComments((prev) => applyCommentDeleted(prev, card.id, busEvent.event));
          break;
      }
    });

    return unsubscribe;
  }, [isOpen, card.id, boardId]);

  useEffect(() => {
    setStartDate(card.start_date ? card.start_date.substring(0, 10) : '');
    setDueDate(card.due_date ? card.due_date.substring(0, 10) : '');
  }, [card.id, card.start_date, card.due_date]);

  // Resynchronise la description distante hors édition locale.
  useEffect(() => {
    if (isEditingDescription) return;
    setDescription(card.description || '');
  }, [card.id, card.description, isEditingDescription]);

  // Resynchronise la couleur de couverture distante.
  useEffect(() => {
    setLabelColor(card.label_color || '');
  }, [card.id, card.label_color]);

  // L'historique est synchronisé ici via cardModalEventBus.
  useEffect(() => {
    if (!isOpen) return;

    const unsubscribe = subscribeToCardModalEvents((busEvent) => {
      if (busEvent.type !== 'activity-created') return;
      if (busEvent.event.board_id !== undefined && boardId !== undefined && busEvent.event.board_id !== boardId) return;

      setActivities((prev) => applyCardActivityCreated(prev, card.id, busEvent.event));
    });

    return unsubscribe;
  }, [isOpen, card.id, boardId]);

  const loadComments = async () => {
    setIsLoadingComments(true);
    try {
      const response = await commentApi.getAll(card.id);
      console.log('Comments loaded:', response.data);
      console.log('First comment user:', response.data[0]?.user);
      setComments(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des commentaires', err);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const loadActivities = async () => {
    setIsLoadingActivities(true);
    try {
      const response = await activityApi.getAll(card.id);
      setActivities(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des activités', err);
    } finally {
      setIsLoadingActivities(false);
    }
  };

  const loadAttachments = async () => {
    setIsLoadingAttachments(true);
    try {
      const response = await attachmentApi.getAll(card.id);
      setAttachments(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des pièces jointes', err);
    } finally {
      setIsLoadingAttachments(false);
    }
  };

  const handleAssignmentsChange = () => {
    // Recharger les activités
    loadActivities();
    // Rafraîchir les données du board pour mettre à jour les avatars
    onRefresh?.();
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await commentApi.create(card.id, { content: newComment.trim() });
      setComments([response.data, ...comments]);
      setNewComment('');
      await loadActivities();
    } catch (err: any) {
      console.error('Erreur lors de l\'ajout du commentaire', err);
      if (err.response?.status === 403) {
        await onRefresh?.();
        if (!wasBoardAccessDenied()) {
          window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de commenter cette carte.'));
        }
      }
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content);
  };

  const handleSaveComment = async (commentId: number) => {
  if (!editingCommentContent.trim()) return;

  try {
    const response = await commentApi.update(card.id, commentId, {
      content: editingCommentContent.trim()
    });

    setComments(prevComments =>
      prevComments.map(comment =>
        comment.id === commentId
          ? { ...comment, ...response.data }
          : comment
      )
    );

    setEditingCommentId(null);
    setEditingCommentContent('');
    await loadActivities();
  } catch (err: any) {
    console.error('Erreur lors de la modification du commentaire', err);
    if (err.response?.status === 403) {
      await onRefresh?.();
      if (!wasBoardAccessDenied()) {
        window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de modifier ce commentaire.'));
      }
    }
  }
};

  const handleDeleteComment = (commentId: number) => {
    setCommentToDelete(commentId);
  };

  const handleConfirmDeleteComment = async () => {
    const commentId = commentToDelete;
    setCommentToDelete(null);
    if (commentId === null) return;

    try {
      await commentApi.delete(card.id, commentId);
      setComments(comments.filter(c => c.id !== commentId));
      await loadActivities();
    } catch (err: any) {
      console.error('Erreur lors de la suppression du commentaire', err);
      if (err.response?.status === 403) {
        await onRefresh?.();
        if (!wasBoardAccessDenied()) {
          window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de supprimer ce commentaire.'));
        }
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  if (!isOpen) return null;

  const handleSaveTitle = async () => {
    if (title.trim()) {
      const truncatedTitle = title.trim().substring(0, 144);
      await onUpdate({ ...card, title: truncatedTitle });
      setIsEditingTitle(false);
      await loadActivities();
    }
  };

  const handleSaveDescription = async () => {
    await onUpdate({ ...card, description: description.trim() });
    setIsEditingDescription(false);
    await loadActivities();
  };

  const handleSaveStartDate = async (date: string) => {
    const previousStartDate = startDate;
    setStartDate(date);

    try {
      await cardApi.update(card.column_id, card.id, { start_date: date || null });
      onRefresh?.();
      await loadActivities();
    } catch (err: any) {
      setStartDate(previousStartDate);
      if (err.response?.status === 403) {
        await onRefresh?.();
        if (!wasBoardAccessDenied()) {
          window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de modifier cette carte.'));
        }
      } else if (err.response?.status === 422) {
        window.alert(getCardApiErrorMessage(err, 'La date de début doit être antérieure ou égale à la date d\'échéance.'));
      } else {
        console.error('Erreur lors de la mise à jour de la date de début', err);
      }
    }
  };

  const handleSaveDueDate = async (date: string) => {
    const previousDueDate = dueDate;
    setDueDate(date);

    try {
      await cardApi.update(card.column_id, card.id, { due_date: date || null });
      onRefresh?.();
      await loadActivities();
    } catch (err: any) {
      setDueDate(previousDueDate);
      if (err.response?.status === 403) {
        await onRefresh?.();
        if (!wasBoardAccessDenied()) {
          window.alert(getCardApiErrorMessage(err, 'Vous n\'avez pas la permission de modifier cette carte.'));
        }
      } else if (err.response?.status === 422) {
        window.alert(getCardApiErrorMessage(err, 'La date d\'échéance doit être postérieure ou égale à la date de début.'));
      } else {
        console.error('Erreur lors de la mise à jour de la date d\'échéance', err);
      }
    }
  };

  const handleColorChange = (color: string) => {
    const newColor = labelColor === color ? '' : color;
    setLabelColor(newColor);
    onUpdate({ ...card, label_color: newColor });
  };

  const handleDelete = () => {
    setShowDeleteCardConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteCardConfirm(false);
    onDelete(card.id);
    onClose();
  };

  const handleArchive = () => {
    setShowArchiveConfirm(true);
  };

  const handleConfirmArchive = () => {
    setShowArchiveConfirm(false);
    const newArchivedState = !card.archived;
    onArchive(card.id, newArchivedState);
    onClose();
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?card=${card.id}`;
    
    try {
      await navigator.clipboard.writeText(url);
      setShowCopiedFeedback(true);
      setTimeout(() => setShowCopiedFeedback(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie du lien', err);
    }
  };

  const handleGeneratePDF = () => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginX = 18;
    const contentWidth = pageWidth - marginX * 2;

    const topMargin = 18;
    const bottomMargin = 22;

    let yPosition = topMargin;

    // -------------------------------------------
    // OUTILS
    // -------------------------------------------

    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          }
        : {
            r: 62,
            g: 110,
            b: 140
          };
    };

    const mainColor = card.label_color
      ? hexToRgb(card.label_color)
      : {
          r: 62,
          g: 110,
          b: 140
        };

    const formatDate = (date: string) => {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    const formatDateTime = (date: string) => {
      return new Date(date).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getActivityLabel = (action: string) => {
      switch (action) {
        case 'created':
          return 'Carte créée';

        case 'title_changed':
          return 'Titre modifié';

        case 'description_changed':
          return 'Description modifiée';

        case 'start_date_changed':
          return 'Date de début modifiée';

        case 'due_date_changed':
          return 'Date d’échéance modifiée';

        case 'moved':
          return 'Carte déplacée';

        case 'archived':
          return 'Carte archivée';

        case 'unarchived':
          return 'Carte désarchivée';

        case 'commented':
          return 'Commentaire ajouté';

        case 'assigned_user':
          return 'Membre affecté';

        case 'unassigned_user':
          return 'Membre retiré';

        case 'added_label':
          return 'Étiquette ajoutée';

        case 'removed_label':
          return 'Étiquette supprimée';

        case 'updated_label':
          return 'Étiquette modifiée';

        default:
          return 'Action effectuée';
      }
    };

    const getFileTypeLabel = (fileType: string) => {
      switch (fileType) {
        case 'image':
          return 'Image';

        case 'video':
          return 'Vidéo';

        case 'document':
          return 'Document';

        case 'archive':
          return 'Archive';

        default:
          return 'Fichier';
      }
    };

    // -------------------------------------------
    // EN-TÊTE
    // -------------------------------------------

    const drawHeader = (firstPage = false) => {
      if (firstPage) {
        doc.setFillColor(mainColor.r, mainColor.g, mainColor.b);
        doc.roundedRect(
          marginX,
          12,
          contentWidth,
          28,
          4,
          4,
          'F'
        );

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);

        const titleLines = doc.splitTextToSize(
          card.title,
          contentWidth - 30
        );

        const titleHeight = titleLines.length * 7;
        const titleStart = 27 - titleHeight / 2 + 4;

        titleLines.forEach((line: string, index: number) => {
          doc.text(
            line,
            pageWidth / 2,
            titleStart + index * 7,
            {
              align: 'center'
            }
          );
        });

        if (card.archived) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');

          doc.text(
            'ARCHIVÉE',
            pageWidth - marginX - 5,
            36,
            {
              align: 'right'
            }
          );
        }

        doc.setTextColor(0, 0, 0);

        yPosition = 48;
      } else {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(
          mainColor.r,
          mainColor.g,
          mainColor.b
        );

        const shortTitle =
          card.title.length > 70
            ? `${card.title.substring(0, 67)}...`
            : card.title;

        doc.text(shortTitle, marginX, 12);

        doc.setDrawColor(
          mainColor.r,
          mainColor.g,
          mainColor.b
        );

        doc.line(
          marginX,
          15,
          pageWidth - marginX,
          15
        );

        doc.setTextColor(0, 0, 0);

        yPosition = 23;
      }
    };

    // -------------------------------------------
    // GESTION DES PAGES
    // -------------------------------------------

    const addPage = () => {
      doc.addPage();
      drawHeader(false);
    };

    const ensureSpace = (requiredHeight: number) => {
      if (
        yPosition + requiredHeight >
        pageHeight - bottomMargin
      ) {
        addPage();
      }
    };

    // -------------------------------------------
    // TITRE DE SECTION
    // -------------------------------------------

    const drawSectionTitle = (
      title: string,
      minimumSpaceAfter = 12
    ) => {
      ensureSpace(13 + minimumSpaceAfter);

      doc.setFillColor(
        mainColor.r,
        mainColor.g,
        mainColor.b
      );

      doc.roundedRect(
        marginX,
        yPosition,
        contentWidth,
        9,
        2,
        2,
        'F'
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);

      doc.text(
        title,
        marginX + 5,
        yPosition + 6
      );

      doc.setTextColor(0, 0, 0);

      yPosition += 14;
    };

    // -------------------------------------------
    // PREMIÈRE PAGE
    // -------------------------------------------

    drawHeader(true);

    // -------------------------------------------
    // INFORMATIONS GÉNÉRALES
    // -------------------------------------------

    const hasDates = card.start_date || card.due_date;

    if (hasDates) {
      drawSectionTitle('DATES', 12);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');

      if (card.start_date) {
        doc.setTextColor(100, 100, 100);
        doc.text(
          'Début',
          marginX + 4,
          yPosition
        );

        doc.setTextColor(0, 0, 0);
        doc.text(
          formatDate(card.start_date),
          marginX + 32,
          yPosition
        );

        yPosition += 6;
      }

      if (card.due_date) {
        doc.setTextColor(100, 100, 100);
        doc.text(
          'Échéance',
          marginX + 4,
          yPosition
        );

        doc.setTextColor(0, 0, 0);
        doc.text(
          formatDate(card.due_date),
          marginX + 32,
          yPosition
        );

        yPosition += 6;
      }

      yPosition += 3;
    }

    // -------------------------------------------
    // AFFECTATIONS
    // -------------------------------------------

    if (card.users && card.users.length > 0) {
      drawSectionTitle(
        `AFFECTATIONS (${card.users.length})`,
        10
      );

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');

      card.users.forEach((assignedUser) => {
        ensureSpace(7);

        doc.setFillColor(
          mainColor.r,
          mainColor.g,
          mainColor.b
        );

        doc.circle(
          marginX + 4,
          yPosition - 1,
          1.3,
          'F'
        );

        doc.text(
          assignedUser.name,
          marginX + 9,
          yPosition
        );

        yPosition += 6;
      });

      yPosition += 3;
    }

    // -------------------------------------------
    // ÉTIQUETTES
    // -------------------------------------------

    if (card.labels && card.labels.length > 0) {
      drawSectionTitle(
        `ÉTIQUETTES (${card.labels.length})`,
        12
      );

      let xPosition = marginX;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');

      card.labels.forEach((label) => {
        const labelColor = hexToRgb(label.color);

        const labelWidth =
          Math.min(
            doc.getTextWidth(label.name) + 10,
            contentWidth
          );

        if (
          xPosition + labelWidth >
          pageWidth - marginX
        ) {
          yPosition += 9;
          xPosition = marginX;

          ensureSpace(10);
        }

        doc.setFillColor(
          labelColor.r,
          labelColor.g,
          labelColor.b
        );

        doc.roundedRect(
          xPosition,
          yPosition - 5,
          labelWidth,
          7,
          2,
          2,
          'F'
        );

        doc.setTextColor(255, 255, 255);

        doc.text(
          label.name,
          xPosition + 5,
          yPosition
        );

        doc.setTextColor(0, 0, 0);

        xPosition += labelWidth + 4;
      });

      yPosition += 10;
    }

    // -------------------------------------------
    // DESCRIPTION
    // -------------------------------------------

    if (card.description) {
      drawSectionTitle('DESCRIPTION', 20);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');

      const cleanDescription = card.description
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/`(.*?)`/g, '$1');

      const descriptionLines = doc.splitTextToSize(
        cleanDescription,
        contentWidth - 12
      );

      descriptionLines.forEach((line: string) => {
        ensureSpace(6);

        doc.text(
          line,
          marginX + 6,
          yPosition
        );

        yPosition += 5;
      });

      yPosition += 5;
    }

    // -------------------------------------------
    // COMMENTAIRES
    // -------------------------------------------

    if (comments.length > 0) {
      drawSectionTitle(
        `COMMENTAIRES (${comments.length})`,
        20
      );

      comments.forEach((comment) => {
        const commentLines = doc.splitTextToSize(
          comment.content,
          contentWidth - 14
        );

        const boxHeight =
          Math.max(
            17,
            12 + commentLines.length * 5
          );

        ensureSpace(boxHeight + 5);

        doc.setFillColor(247, 248, 250);

        doc.setDrawColor(225, 228, 232);

        doc.roundedRect(
          marginX,
          yPosition,
          contentWidth,
          boxHeight,
          3,
          3,
          'FD'
        );

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');

        doc.setTextColor(
          mainColor.r,
          mainColor.g,
          mainColor.b
        );

        doc.text(
          comment.user?.name || 'Utilisateur',
          marginX + 5,
          yPosition + 6
        );

        doc.setFont(
          'helvetica',
          'normal'
        );

        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);

        doc.text(
          formatDateTime(comment.created_at),
          pageWidth - marginX - 5,
          yPosition + 6,
          {
            align: 'right'
          }
        );

        doc.setTextColor(30, 30, 30);
        doc.setFontSize(9);

        doc.text(
          commentLines,
          marginX + 5,
          yPosition + 13
        );

        yPosition += boxHeight + 5;
      });

      yPosition += 2;
    }

    // -------------------------------------------
    // ACTIVITÉ RÉCENTE
    // -------------------------------------------

    if (activities.length > 0) {
      drawSectionTitle(
        `ACTIVITÉ RÉCENTE (${Math.min(
          activities.length,
          10
        )})`,
        20
      );

      const recentActivities = activities.slice(0, 10);

      const tableX = marginX;
      const tableWidth = contentWidth;

      const dateWidth = 42;
      const userWidth = 45;
      const actionWidth =
        tableWidth - dateWidth - userWidth;

      ensureSpace(9);

      doc.setFillColor(
        mainColor.r,
        mainColor.g,
        mainColor.b
      );

      doc.rect(
        tableX,
        yPosition,
        tableWidth,
        8,
        'F'
      );

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');

      doc.text(
        'Date',
        tableX + 4,
        yPosition + 5
      );

      doc.text(
        'Utilisateur',
        tableX + dateWidth + 4,
        yPosition + 5
      );

      doc.text(
        'Action',
        tableX + dateWidth + userWidth + 4,
        yPosition + 5
      );

      yPosition += 8;

      recentActivities.forEach(
        (activity, index) => {
          ensureSpace(8);

          if (index % 2 === 0) {
            doc.setFillColor(248, 249, 250);

            doc.rect(
              tableX,
              yPosition,
              tableWidth,
              8,
              'F'
            );
          }

          doc.setFont(
            'helvetica',
            'normal'
          );

          doc.setFontSize(7.5);
          doc.setTextColor(40, 40, 40);

          const activityDate =
            new Date(
              activity.created_at
            ).toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            });

          doc.text(
            activityDate,
            tableX + 4,
            yPosition + 5
          );

          const activityUser =
            activity.user?.name ||
            'Utilisateur';

          const shortUser =
            activityUser.length > 20
              ? `${activityUser.substring(
                  0,
                  17
                )}...`
              : activityUser;

          doc.text(
            shortUser,
            tableX + dateWidth + 4,
            yPosition + 5
          );

          const actionLabel =
            getActivityLabel(
              activity.action
            );

          doc.text(
            actionLabel,
            tableX +
              dateWidth +
              userWidth +
              4,
            yPosition + 5
          );

          yPosition += 8;
        }
      );

      doc.setDrawColor(220, 220, 220);

      doc.rect(
        tableX,
        yPosition -
          recentActivities.length * 8 -
          8,
        tableWidth,
        recentActivities.length * 8 + 8
      );

      yPosition += 7;
    }

    // -------------------------------------------
    // PIÈCES JOINTES
    // -------------------------------------------

    if (attachments.length > 0) {
      drawSectionTitle(
        `PIÈCES JOINTES (${attachments.length})`,
        18
      );

      attachments.forEach((attachment) => {
        ensureSpace(12);

        const fileType =
          getFileTypeLabel(
            attachment.file_type
          );

        doc.setFillColor(247, 248, 250);

        doc.setDrawColor(230, 230, 230);

        doc.roundedRect(
          marginX,
          yPosition,
          contentWidth,
          10,
          2,
          2,
          'FD'
        );

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');

        doc.setTextColor(
          mainColor.r,
          mainColor.g,
          mainColor.b
        );

        doc.text(
          fileType,
          marginX + 5,
          yPosition + 6
        );

        doc.setFont(
          'helvetica',
          'normal'
        );

        doc.setTextColor(30, 30, 30);

        const maxFilenameWidth =
          contentWidth - 70;

        const filenameLines =
          doc.splitTextToSize(
            attachment.filename,
            maxFilenameWidth
          );

        const displayedFilename =
          filenameLines[0];

        doc.text(
          displayedFilename,
          marginX + 30,
          yPosition + 6
        );

        doc.setTextColor(100, 100, 100);

        doc.text(
          attachment.formatted_size,
          pageWidth - marginX - 5,
          yPosition + 6,
          {
            align: 'right'
          }
        );

        yPosition += 13;
      });

      yPosition += 2;
    }

    // -------------------------------------------
    // PIEDS DE PAGE
    // -------------------------------------------

    const totalPages = doc.getNumberOfPages();

    for (
      let pageNumber = 1;
      pageNumber <= totalPages;
      pageNumber++
    ) {
      doc.setPage(pageNumber);

      const footerY = pageHeight - 12;

      doc.setDrawColor(220, 220, 220);

      doc.line(
        marginX,
        footerY - 5,
        pageWidth - marginX,
        footerY - 5
      );

      doc.setFontSize(7.5);
      doc.setFont(
        'helvetica',
        'normal'
      );

      doc.setTextColor(120, 120, 120);

      doc.text(
        `Créée le ${formatDate(
          card.created_at
        )}`,
        marginX,
        footerY
      );

      doc.text(
        `Modifiée le ${formatDate(
          card.updated_at
        )}`,
        pageWidth / 2,
        footerY,
        {
          align: 'center'
        }
      );

      doc.text(
        `Page ${pageNumber} / ${totalPages}`,
        pageWidth - marginX,
        footerY,
        {
          align: 'right'
        }
      );
    }

    // -------------------------------------------
    // TÉLÉCHARGEMENT
    // -------------------------------------------

    const safeTitle = card.title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9-_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .toLowerCase();

    const fileName =
      `carte-${safeTitle || card.id}.pdf`;

    doc.save(fileName);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (action: string) => {
    const iconProps = { width: 16, height: 16, strokeWidth: 2 };
    switch (action) {
      case 'created':
        return <Plus {...iconProps} color="#0079BF" />;
      case 'title_changed':
        return <EditPencil {...iconProps} color="#FF991F" />;
      case 'description_changed':
        return <Page {...iconProps} color="#6554C0" />;
      case 'moved':
        return <Check {...iconProps} color="#00875A" />;
      case 'archived':
        return <BoxIso {...iconProps} color="#FF991F" />;
      case 'unarchived':
        return <BoxIso {...iconProps} color="#00875A" />;
      case 'commented':
        return <Xmark {...iconProps} color="#5E6C84" />;
      case 'attachment_added':
        return <Plus {...iconProps} color="#0079BF" />;
      case 'attachment_removed':
        return <Xmark {...iconProps} color="#DE350B" />;
      default:
        return <Page {...iconProps} color="#5E6C84" />;
    }
  };

  const getActivityText = (activity: CardActivity) => {
    const userName = activity.user?.name || 'Utilisateur inconnu';
    
    switch (activity.action) {
      case 'created':
        return <><strong>{userName}</strong> a créé cette carte</>;
      case 'title_changed':
        return (
          <>
            <strong>{userName}</strong> a modifié le titre
            {activity.old_value && activity.new_value && (
              <div className="activity-detail">
                <span className="activity-old">"{activity.old_value}"</span>
                {' → '}
                <span className="activity-new">"{activity.new_value}"</span>
              </div>
            )}
          </>
        );
      case 'description_changed': {
        const actionText = !activity.old_value
          ? 'a ajouté une description'
          : 'a modifié la description';

        return <><strong>{userName}</strong> {actionText}</>;
      }
      case 'start_date_changed': {
        const actionText = !activity.old_value
          ? 'a ajouté une date de début'
          : !activity.new_value
            ? 'a supprimé la date de début'
            : 'a modifié la date de début';

        return (
          <>
            <strong>{userName}</strong> {actionText}
            {(activity.old_value || activity.new_value) && (
              <div className="activity-detail">
                <span className="activity-old">
                  {activity.old_value
                    ? new Date(activity.old_value).toLocaleDateString('fr-FR')
                    : 'Aucune date'}
                </span>
                {' → '}
                <span className="activity-new">
                  {activity.new_value
                    ? new Date(activity.new_value).toLocaleDateString('fr-FR')
                    : 'Aucune date'}
                </span>
              </div>
            )}
          </>
        );
      }
      case 'due_date_changed': {
        const actionText = !activity.old_value
          ? 'a ajouté une date d\'échéance'
          : !activity.new_value
            ? 'a supprimé la date d\'échéance'
            : 'a modifié la date d\'échéance';

        return (
          <>
            <strong>{userName}</strong> {actionText}
            {(activity.old_value || activity.new_value) && (
              <div className="activity-detail">
                <span className="activity-old">
                  {activity.old_value
                    ? new Date(activity.old_value).toLocaleDateString('fr-FR')
                    : 'Aucune date'}
                </span>
                {' → '}
                <span className="activity-new">
                  {activity.new_value
                    ? new Date(activity.new_value).toLocaleDateString('fr-FR')
                    : 'Aucune date'}
                </span>
              </div>
            )}
          </>
        );
      }
      case 'moved':
        return (
          <>
            <strong>{userName}</strong> a déplacé la carte
            {activity.old_value && activity.new_value && (
              <div className="activity-detail">
                de <span className="activity-old">{activity.old_value}</span>
                {' vers '}
                <span className="activity-new">{activity.new_value}</span>
              </div>
            )}
          </>
        );
      case 'archived':
        return <><strong>{userName}</strong> a archivé cette carte</>;
      case 'unarchived':
        return <><strong>{userName}</strong> a désarchivé cette carte</>;
      case 'commented':
        return <><strong>{userName}</strong> a ajouté un commentaire</>;
      case 'assigned_user':
        return (
          <>
            <strong>{userName}</strong> a affecté la carte à <strong>{activity.new_value}</strong>
          </>
        );
      case 'unassigned_user':
        return (
          <>
            <strong>{userName}</strong> a retiré <strong>{activity.old_value}</strong> de la carte
          </>
        );
      case 'added_label':
        return (
          <>
            <strong>{userName}</strong> a ajouté l'étiquette{' '}
            <span 
              className="activity-label-badge" 
              style={{ backgroundColor: activity.metadata?.label_color || '#ccc' }}
            >
              {activity.new_value}
            </span>
          </>
        );
      case 'removed_label':
        return (
          <>
            <strong>{userName}</strong> a supprimé l'étiquette{' '}
            <span 
              className="activity-label-badge" 
              style={{ backgroundColor: activity.metadata?.label_color || '#ccc' }}
            >
              {activity.old_value}
            </span>
          </>
        );
      case 'updated_label':
        return (
          <>
            <strong>{userName}</strong> a modifié l'étiquette
            {activity.old_value && activity.new_value && (
              <div className="activity-detail">
                <span className="activity-old">"{activity.old_value}"</span>
                {' → '}
                <span className="activity-new">"{activity.new_value}"</span>
              </div>
            )}
          </>
        );
      case 'attachment_added':
        return (
          <>
            <strong>{userName}</strong> a ajouté la pièce jointe
            {activity.new_value && <> "{activity.new_value}"</>}
          </>
        );

      case 'attachment_removed':
        return (
          <>
            <strong>{userName}</strong> a supprimé la pièce jointe
            {activity.old_value && <> "{activity.old_value}"</>}
          </>
        );
      default:
        return <><strong>{userName}</strong> a effectué une action</>;
    }
  };

  return (
    <>
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container">
        {labelColor && (
          <div className="card-label-banner" style={{ backgroundColor: labelColor }}>
            {canEdit && (
              <button 
                className="btn-color-picker"
                onClick={() => setShowColorPalette(!showColorPalette)}
                title="Changer la couleur"
              >
                <EditPencil width={20} height={20} strokeWidth={2} color="white" />
              </button>
            )}
          </div>
        )}
        {!labelColor && canEdit && (
          <div className="card-label-banner-empty">
            <button 
              className="btn-color-picker"
              onClick={() => setShowColorPalette(!showColorPalette)}
              title="Ajouter une couleur"
            >
              <Plus width={20} height={20} strokeWidth={2} />
            </button>
          </div>
        )}
        
        {/* Palette de couleurs déroulante */}
        {showColorPalette && canEdit && (
          <div className="color-palette-dropdown">
            <div className="color-palette-header">
              <h4>Couleur de la carte</h4>
              <button onClick={() => setShowColorPalette(false)}>
                <Xmark width={16} height={16} strokeWidth={2} />
              </button>
            </div>
            <div className="card-color-palette">
              {LABEL_COLORS.map((color) => (
                <button
                  key={color.value}
                  className={`card-color-option ${labelColor === color.value ? 'selected' : ''}`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => {
                    handleColorChange(color.value);
                    setShowColorPalette(false);
                  }}
                  title={color.name}
                >
                  {labelColor === color.value && (
                    <Check width={18} height={18} strokeWidth={2} color="white" />
                  )}
                </button>
              ))}
              {labelColor && (
                <button
                  className="card-color-remove"
                  onClick={() => {
                    handleColorChange('');
                    setShowColorPalette(false);
                  }}
                  title="Retirer la couleur"
                >
                  <Xmark width={16} height={16} strokeWidth={2} />
                </button>
              )}
            </div>
          </div>
        )}
        <div className="modal-header-buttons">
          <button 
            className="modal-pdf" 
            onClick={handleGeneratePDF} 
            title="Télécharger en PDF"
          >
            <Download width={20} height={20} strokeWidth={2} />
          </button>
          <button 
            className="modal-link" 
            onClick={handleCopyLink} 
            title="Copier le lien"
          >
            {showCopiedFeedback ? (
              <Check width={20} height={20} strokeWidth={2} />
            ) : (
              <Link width={20} height={20} strokeWidth={2} />
            )}
          </button>
          {(userRole === 'admin' || userRole === 'editor') && (
            <button 
              className="modal-archive" 
              onClick={handleArchive} 
              title={card.archived ? "Désarchiver la carte" : "Archiver la carte"}
            >
              <BoxIso width={20} height={20} strokeWidth={2} />
            </button>
          )}
          {userRole === 'admin' && !card.archived && (
            <button className="modal-delete" onClick={handleDelete} title="Supprimer la carte">
              <Trash width={20} height={20} strokeWidth={2} />
            </button>
          )}
          <button className="modal-close" onClick={onClose} title="Fermer">
            <Xmark width={20} height={20} strokeWidth={2} />
          </button>
        </div>
        
        <div className="modal-content">
          {/* Section gauche */}
          <div className="modal-left">
            {/* Titre */}
            <div className="modal-header">
              {isEditingTitle ? (
                <div className="modal-title-edit">
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={handleSaveTitle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle();
                      if (e.key === 'Escape') {
                        setTitle(card.title);
                        setIsEditingTitle(false);
                      }
                    }}
                    maxLength={144}
                    autoFocus
                    className="modal-title-input"
                  />
                </div>
              ) : (
                <h2 
                  className="modal-title" 
                  onClick={() => canEdit && setIsEditingTitle(true)}
                  style={{ cursor: canEdit ? 'pointer' : 'default' }}
                >
                  <span className="modal-title-icon">○</span>
                  {card.title}
                </h2>
              )}
            </div>

            {/* Message pour les cartes archivées */}
            {card.archived && (
              <div className="modal-archived-notice">
                <BoxIso width={18} height={18} strokeWidth={2} />
                <span>
                  {(userRole === 'admin' || userRole === 'editor')
                    ? "Cette carte est archivée. Désarchivez-la pour pouvoir la modifier."
                    : "Cette carte est archivée."}
                </span>
              </div>
            )}

            {/* Membres affectés */}
            {boardId && (
              <div className="modal-section">
                <CardAssignments
                  cardId={card.id}
                  boardId={boardId}
                  canEdit={canEdit}
                  onAssignmentsChange={handleAssignmentsChange}
                  remoteUsers={card.users}
                />
              </div>
            )}

            {/* Étiquettes */}
              <div className="modal-section">
                <LabelManager
                  cardId={card.id}
                  boardId={boardId}
                  canEdit={canEdit}
                  onLabelsChange={() => {
                    loadActivities();
                    onRefresh?.();
                  }}
                  remoteLabels={card.labels}
                />
              </div>


            {/* Dates */}
            <div className="modal-section">
              <h3>Dates</h3>
              <div className="modal-dates">
                <div className="date-field">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleSaveStartDate(e.target.value)}
                    disabled={!canEdit}
                    max={dueDate || undefined}
                    className="date-input"
                  />
                </div>
                <div className="date-field">
                  <label>Date d'échéance</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => handleSaveDueDate(e.target.value)}
                    disabled={!canEdit}
                    min={startDate || undefined}
                    className="date-input"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="modal-section">
              <div className="modal-section-header">
                <h3>Description</h3>
                {!isEditingDescription && canEdit && (
                  <button 
                    className="btn-edit-section"
                    onClick={() => setIsEditingDescription(true)}
                  >
                    Modifier
                  </button>
                )}
              </div>
              
              {isEditingDescription ? (
                <div>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ajouter une description plus détaillée..."
                    className="modal-textarea"
                    rows={6}
                    autoFocus
                  />
                  <div className="modal-actions">
                    <button className="btn btn-primary" onClick={handleSaveDescription}>
                      Enregistrer
                    </button>
                    <button 
                      className="btn btn-cancel" 
                      onClick={() => {
                        setDescription(card.description || '');
                        setIsEditingDescription(false);
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="modal-description"
                  onClick={() => canEdit && setIsEditingDescription(true)}
                  style={{ cursor: canEdit ? 'pointer' : 'default' }}
                >
                  {description ? (
                    <ReactMarkdown>{description}</ReactMarkdown>
                  ) : (
                    <span className="description-placeholder">
                      {canEdit ? 'Ajouter une description plus détaillée...' : 'Aucune description'}
                    </span>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* Section droite - Activité */}
          <div className="modal-right">
            <h3 className="modal-sidebar-title">Activité</h3>
            
            <div className="modal-activity">
              {isLoadingActivities ? (
                <div className="activity-loading">Chargement des activités...</div>
              ) : activities.length > 0 ? (
                activities.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">{getActivityIcon(activity.action)}</div>
                    <div className="activity-content">
                      <div className="activity-text">
                        {getActivityText(activity)}
                      </div>
                      <div className="activity-date">
                        {formatDate(activity.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="activity-empty">Aucune activité pour le moment</div>
              )}

              {/* Pièces jointes */}
              <AttachmentList
                cardId={card.id}
                canEdit={canEdit}
                onAttachmentsChange={() => {
                  loadAttachments();
                  loadActivities();
                }}
              />

              <div className="modal-section">
                <h4 className="modal-subsection-title">Commentaires</h4>
                
                {/* Formulaire d'ajout de commentaire */}
                {!card.archived && (
                  <div className="comment-form">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Écrire un commentaire..."
                      className="modal-comment-input"
                      rows={3}
                    />
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                    >
                      Commenter
                    </button>
                  </div>
                )}

                {/* Liste des commentaires */}
                <div className="comments-list">
                  {isLoadingComments ? (
                    <div className="comment-loading">Chargement des commentaires...</div>
                  ) : comments.length === 0 ? (
                    <div className="comment-empty">Aucun commentaire pour le moment</div>
                  ) : (
                    comments.map(comment => (
                      <div key={comment.id} className="comment-item">
                        <div className="comment-header">
                          <div className="comment-author">
                            <div className="comment-avatar">
                              {(() => {
                                return comment.user?.avatar_url ? (
                                  <img src={comment.user.avatar_url} alt={comment.user.name} className="comment-avatar-image" />
                                ) : (
                                  comment.user?.name?.charAt(0).toUpperCase() || '💬'
                                );
                              })()}
                            </div>
                            <div>
                              <div className="comment-author-name">
                                {comment.user?.name || 'Utilisateur inconnu'}
                              </div>
                              <div className="comment-date">
                                Créé le {formatDate(comment.created_at)}
                                {comment.updated_at &&
                                  new Date(comment.updated_at).getTime() !== new Date(comment.created_at).getTime() && (
                                    <> — Modifié le {formatDate(comment.updated_at)}</>
                                  )}
                              </div>
                            </div>
                          </div>
                          <div className="comment-actions">
                            {editingCommentId !== comment.id && !card.archived && (
                              <>
                                {/* Modifier uniquement son propre commentaire */}
                                {comment.user?.id === user?.id && (
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleEditComment(comment)}
                                    title="Modifier"
                                  >
                                    <EditPencil width={16} height={16} strokeWidth={2} />
                                  </button>
                                )}

                                {/* Supprimer son commentaire, ou tous les commentaires pour l'admin */}
                                {(comment.user?.id === user?.id || userRole === 'admin') && (
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleDeleteComment(comment.id)}
                                    title="Supprimer"
                                  >
                                    <Trash width={16} height={16} strokeWidth={2} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {editingCommentId === comment.id ? (
                          <div className="comment-edit">
                            <textarea
                              value={editingCommentContent}
                              onChange={(e) => setEditingCommentContent(e.target.value)}
                              className="modal-comment-input"
                              rows={3}
                              autoFocus
                            />
                            <div className="modal-actions">
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => handleSaveComment(comment.id)}
                              >
                                Enregistrer
                              </button>
                              <button 
                                className="btn btn-cancel btn-sm"
                                onClick={handleCancelEdit}
                              >
                                Annuler
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="comment-content">
                            {comment.content}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={showDeleteCardConfirm}
      title="Supprimer la carte"
      message="Voulez-vous vraiment supprimer cette carte ?"
      confirmText="Supprimer"
      onConfirm={handleConfirmDelete}
      onCancel={() => setShowDeleteCardConfirm(false)}
      variant="danger"
    />

    <ConfirmModal
      isOpen={showArchiveConfirm}
      title={card.archived ? 'Désarchiver la carte' : 'Archiver la carte'}
      message={card.archived
        ? 'Voulez-vous vraiment désarchiver cette carte ?'
        : 'Voulez-vous vraiment archiver cette carte ?'}
      confirmText={card.archived ? 'Désarchiver' : 'Archiver'}
      onConfirm={handleConfirmArchive}
      onCancel={() => setShowArchiveConfirm(false)}
    />

    <ConfirmModal
      isOpen={commentToDelete !== null}
      title="Supprimer le commentaire"
      message="Voulez-vous vraiment supprimer ce commentaire ?"
      confirmText="Supprimer"
      onConfirm={handleConfirmDeleteComment}
      onCancel={() => setCommentToDelete(null)}
      variant="danger"
    />
    </>
  );
};
